import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ToastMessage } from 'components/primitive';
import { ToastStatus } from '@/common/enum';
import { JPDAIncidentSettingValueType } from 'interfaces/incidents.interface.ts';
import { CheckSelectOptions } from 'interfaces/SelectOptions.interface.ts';
import { useAppDispatch, useAppSelector } from 'store/hooks.ts';
import { JPDAIncidentSettingValueSelector } from 'store/feature/incidents/incidents.selector.ts';
import { allGroupsSelector } from 'store/feature/accountsInfo/accountsInfo.selector.ts';
import {
  fetchJPDAIncidentSetting,
  saveJPDAIncidentSetting,
} from 'store/feature/incidents/incidents.thunk.ts';

export type JPDASettingType = 'IA' | 'PA';
export type JPDAActionType = 'Begin' | 'Complete';

const defaultJPDAIncidentSettingValue: JPDAIncidentSettingValueType = {
  pa: [],
  ia: [],
};

/**
 * The JPDA Start / End workflow: the group counts the side menu's JPDA buttons show, and the state
 * and save behind ModalJPDASelectGroup. It lives in DrawerMenu, outside the menu panel, because the
 * menu closes before the modal opens (the panel traps focus), which unmounts the buttons.
 */
const useJPDAWorkflow = () => {
  const dispatch = useAppDispatch();
  const allGroups = useAppSelector(allGroupsSelector);
  const jpdaSetting =
    useAppSelector(JPDAIncidentSettingValueSelector) ?? defaultJPDAIncidentSettingValue;

  const allPalmettoGroups = useMemo(
    () => (allGroups ?? []).filter((group) => group.pvIsAgency === 0),
    [allGroups]
  );
  const paStarted = jpdaSetting.pa;
  const iaStarted = jpdaSetting.ia;
  const paInactive = allPalmettoGroups.filter(
    (group) => !paStarted.includes(group.pvGroupID)
  ).length;
  const iaInactive = allPalmettoGroups.filter(
    (group) => !iaStarted.includes(group.pvGroupID)
  ).length;

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [settingType, setSettingType] = useState<JPDASettingType>('IA');
  const [actionType, setActionType] = useState<JPDAActionType>('Begin');
  const [activeStep, setActiveStep] = useState<1 | 2>(1);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  /** Opens the modal for this action, and returns false when there is no group to act on. */
  const open = (setting: JPDASettingType, action: JPDAActionType): boolean => {
    const started = setting === 'IA' ? iaStarted : paStarted;
    const inactive = setting === 'IA' ? iaInactive : paInactive;
    if (action === 'Complete' && started.length === 0) return false;
    if (action === 'Begin' && inactive === 0) return false;
    setActiveStep(1);
    setSettingType(setting);
    setActionType(action);
    setIsOpen(true);
    return true;
  };

  const onClose = () => {
    setActiveStep(1);
    setIsOpen(false);
  };

  const onSubmit = async (selectedOptions: CheckSelectOptions[]) => {
    if (activeStep === 1) return setActiveStep(2);

    const selected = selectedOptions.map((option) => Number(option.value));
    const started = settingType === 'IA' ? iaStarted : paStarted;
    const next =
      actionType === 'Begin'
        ? [...started, ...selected]
        : started.filter((value) => !selected.includes(value));
    const payload: JPDAIncidentSettingValueType =
      settingType === 'IA' ? { ia: next, pa: paStarted } : { ia: iaStarted, pa: next };

    try {
      setIsSaving(true);
      const result = await dispatch(saveJPDAIncidentSetting(payload)).unwrap();
      if (result) {
        const message =
          actionType === 'Complete' ? 'JPDA Completed successfully' : 'JPDA Started successfully';
        toast.info(<ToastMessage status={ToastStatus.SUCCESS} message={message} />);
        dispatch(fetchJPDAIncidentSetting());
      }
    } catch (error) {
      console.error('JPDA setting save failed', error);
      toast.error(
        <ToastMessage status={ToastStatus.ERROR} message='Failed processing JPDA Update' />
      );
    }

    setIsSaving(false);
    onClose();
  };

  return {
    counts: { paStarted: paStarted.length, paInactive, iaStarted: iaStarted.length, iaInactive },
    open,
    modalProps: {
      steps: activeStep,
      allGroups: allPalmettoGroups,
      jpdaStarted: settingType === 'IA' ? iaStarted : paStarted,
      isOpen,
      settingType,
      actionType,
      isSaving,
      onClose,
      onSubmit,
    },
  };
};

export default useJPDAWorkflow;
